'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Header } from '@/components/layout';
import { Button, Card, CardHeader, Input, Select } from '@/components/ui';
import { api } from '@/lib/api';
import { usePhoneCountryCodes } from '@/lib/phoneCountryCodes';

const signupSchema = z.object({
    first_name: z.string().min(1),
    last_name: z.string().min(1),
    email: z.email(),
    password: z
        .string()
        .min(8)
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/, 'Password must contain at least one special character'),
    confirm_password: z.string().min(1),
    professional_area: z.string().min(1),
    country: z.string().min(2),
    city: z.string().min(1),
    affiliation: z.string().optional(),
    phone_country_code: z.string().optional(),
    phone_number: z.string().optional(),
}).refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
});

type SignupFormData = z.infer<typeof signupSchema>;

interface ProfessionalArea {
    id: string;
    name_en: string;
    name_pt: string;
}

interface Country {
    code: string;
    name_en: string;
    name_pt: string;
}

export default function SignupPage() {
    const t = useTranslations('auth.signup');
    const tProfile = useTranslations('profile');
    const tValidation = useTranslations('validation');
    const locale = useLocale();
    const router = useRouter();
    const phoneCountryCodes = usePhoneCountryCodes();
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [professionalAreas, setProfessionalAreas] = useState<ProfessionalArea[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [areas, countriesList] = await Promise.all([
                    api.getProfessionalAreas(),
                    api.getCountries(),
                ]);
                setProfessionalAreas(areas);
                setCountries(countriesList);
            } catch (err) {
                console.error('Failed to load data:', err);
            }
        };
        loadData();
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useForm<SignupFormData>({
        resolver: zodResolver(signupSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirm_password: '',
            professional_area: '',
            country: '',
            city: '',
            affiliation: '',
            phone_country_code: '',
            phone_number: '',
        },
    });

    const onSubmit = async (data: SignupFormData) => {
        setIsLoading(true);
        setError(null);

        try {
            await api.register(data);
            setSuccess(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    const getPasswordError = () => {
        if (!errors.password) return undefined;
        const msg = errors.password.message;
        if (msg?.includes('uppercase')) return tValidation('passwordUppercase');
        if (msg?.includes('special')) return tValidation('passwordSpecial');
        return tValidation('passwordMin');
    };

    if (success) {
        return (
            <>

                <main className="min-h-screen flex items-center justify-center px-4 pt-4 pb-4 bg-[#F9F9F9]">
                    <div className="w-full max-w-md text-center">
                        <div className="mb-6">
                            <div className="w-16 h-16 mx-auto bg-black rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="text-7xl font-medium text-black mb-4">{t('successTitle')}</h2>
                        <p className="text-[#888888] mb-8 leading-relaxed">
                            {t('successMessage')}
                        </p>
                        <Link href="/login">
                            <Button variant="outline" className="w-full">{t('goToLogin')}</Button>
                        </Link>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>

            <main className="min-h-screen flex items-center justify-center px-4 pt-24 pb-12 bg-[#F9F9F9]">
                <div className="w-full max-w-7xl">

                    <Card className="w-full border-none shadow-none bg-transparent p-0">
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 text-red-600 text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input
                                    label={t('firstName')}
                                    {...register('first_name')}
                                    error={errors.first_name ? tValidation('required') : undefined}
                                    className="bg-white"
                                />
                                <Input
                                    label={t('lastName')}
                                    {...register('last_name')}
                                    error={errors.last_name ? tValidation('required') : undefined}
                                    className="bg-white"
                                />
                            </div>

                            {/* Email and Phone - side by side */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input
                                    label={t('email')}
                                    type="email"
                                    autoComplete="email"
                                    {...register('email')}
                                    error={errors.email ? tValidation('email') : undefined}
                                    className="bg-white"
                                />

                                {/* Phone Number */}
                                <div>
                                    <label className="block text-xs font-medium text-slate-500 mb-1">
                                        {tProfile('phone')}
                                    </label>
                                    <div className="grid grid-cols-[110px_1fr] gap-2">
                                        <Controller
                                            name="phone_country_code"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    value={field.value || ''}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    placeholder="+351"
                                                    className="bg-white"
                                                    options={phoneCountryCodes.map((pc) => ({
                                                        value: pc.value,
                                                        label: pc.label,
                                                        searchText: pc.searchText,
                                                    }))}
                                                />
                                            )}
                                        />
                                        <Input
                                            {...register('phone_number')}
                                            placeholder={tProfile('phonePlaceholder')}
                                            type="tel"
                                            className="bg-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="relative">
                                    <Input
                                        label={t('password')}
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        {...register('password')}
                                        error={getPasswordError()}
                                        className="bg-white"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-[28px] text-slate-400 hover:text-black transition-colors"
                                    >
                                        {showPassword ? (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                <div className="relative">
                                    <Input
                                        label={t('confirmPassword')}
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        {...register('confirm_password')}
                                        error={errors.confirm_password ? tValidation('passwordMatch') : undefined}
                                        className="bg-white"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-[28px] text-slate-400 hover:text-black transition-colors"
                                    >
                                        {showConfirmPassword ? (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <Controller
                                name="professional_area"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label={t('professionalArea')}
                                        {...field}
                                        onChange={(value) => field.onChange(value)}
                                        placeholder="Select..."
                                        className="bg-white"
                                        options={professionalAreas.map((area) => ({
                                            value: area.id,
                                            label: locale === 'pt' ? area.name_pt : area.name_en,
                                        }))}
                                        error={errors.professional_area ? tValidation('required') : undefined}
                                    />
                                )}
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Controller
                                    name="country"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label={t('country')}
                                            {...field}
                                            onChange={(value) => field.onChange(value)}
                                            placeholder="Select..."
                                            className="bg-white"
                                            options={countries.map((country) => ({
                                                value: country.code,
                                                label: locale === 'pt' ? country.name_pt : country.name_en,
                                            }))}
                                            error={errors.country ? tValidation('required') : undefined}
                                        />
                                    )}
                                />
                                <Input
                                    label={t('city')}
                                    {...register('city')}
                                    error={errors.city ? tValidation('required') : undefined}
                                    className="bg-white"
                                />
                            </div>

                            <Input
                                label={t('affiliation')}
                                {...register('affiliation')}
                                className="bg-white"
                            />

                            <Button type="submit" className="w-full" isLoading={isLoading}>
                                {t('submit')}
                            </Button>
                        </form>

                        <div className="mt-8 text-center">
                            <p className="text-sm text-[#888888]">
                                {t('haveAccount')}{' '}
                                <Link
                                    href="/login"
                                    className="text-black font-medium hover:underline"
                                >
                                    {t('loginLink')}
                                </Link>
                            </p>
                        </div>
                    </Card>
                </div>
            </main>
        </>
    );
}
